import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import VerifiedIcon from '@mui/icons-material/Verified';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupsIcon from '@mui/icons-material/Groups';
import aboutImage from '../../assets/images/about/israel.png';

// ============================================================
// ANIMATIONS
// ============================================================

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-12px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(10, 79, 213, 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(10, 79, 213, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(10, 79, 213, 0);
  }
`;

const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// ============================================================
// COLOR PALETTE
// ============================================================

const DEEP_BLUE = "#0a1f44";
const PRIMARY_BLUE = "#0a4fd5";
const LIGHT_BLUE = "#4a9eff";
const SOFT_BLUE = "#e8f2ff";
const BORDER_BLUE = "#d4e5ff";
const TEXT_MUTED = "#5a6b85";

// ============================================================
// STYLED COMPONENTS
// ============================================================

const SectionWrapper = styled(Box)({
  position: "relative",
  padding: "80px 20px",
  backgroundColor: "#ffffff",
  overflow: "hidden",
});

const ContentContainer = styled(Box)({
  maxWidth: "1200px",
  margin: "0 auto",
  position: "relative",
  zIndex: 2,
});

const ImageWrapper = styled(Box)({
  position: "relative",
  animation: `${fadeInLeft} 1s ease forwards`,
  padding: "20px",
});

const ImageContainer = styled(Box)({
  position: "relative",
  borderRadius: "24px",
  overflow: "hidden",
  boxShadow: "0 25px 70px rgba(10, 31, 68, 0.15)",
  transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",

  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(135deg, transparent 50%, rgba(10, 79, 213, 0.08) 100%)",
    pointerEvents: "none",
  },

  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 35px 90px rgba(10, 79, 213, 0.25)",

    "& img": {
      transform: "scale(1.05)",
    },
  },
});

const StyledImage = styled("img")({
  width: "100%",
  height: "auto",
  display: "block",
  objectFit: "cover",
  transition: "transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
});

const FloatingCard = styled(Box)(({ position, color }) => ({
  position: "absolute",
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "16px 24px",
  borderRadius: "16px",
  backgroundColor: "#ffffff",
  boxShadow: "0 15px 40px rgba(10, 31, 68, 0.12)",
  border: `1px solid ${BORDER_BLUE}`,
  zIndex: 3,
  animation: `${float} 3.5s ease-in-out infinite`,
  animationDelay: position === "top" ? "0s" : "1.5s",

  ...(position === "top" && {
    top: 40,
    right: 0,
  }),

  ...(position === "bottom" && {
    bottom: 40,
    left: 0,
  }),

  "@media (max-width: 768px)": {
    padding: "12px 18px",
    ...(position === "top" && { top: 20, right: 10 }),
    ...(position === "bottom" && { bottom: 20, left: 10 }),
  },
}));

const FloatingIcon = styled(Box)(({ color }) => ({
  width: 46,
  height: 46,
  borderRadius: "12px",
  backgroundColor: color === "blue" ? SOFT_BLUE : "rgba(74, 158, 255, 0.1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: color === "blue" ? PRIMARY_BLUE : LIGHT_BLUE,
  flexShrink: 0,

  "& svg": {
    fontSize: 24,
  },

  "@media (max-width: 768px)": {
    width: 38,
    height: 38,

    "& svg": {
      fontSize: 20,
    },
  },
}));

const TextContent = styled(Box)({
  animation: `${fadeInRight} 1s ease 0.2s forwards`,
  opacity: 0,
  textAlign: "left",
});

const CTAButton = styled("a")({
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  backgroundColor: "#000000",
  color: "#ffffff",
  padding: "14px 32px",
  borderRadius: "8px",
  fontWeight: 600,
  fontSize: "14px",
  letterSpacing: "0.5px",
  textDecoration: "none",
  textTransform: "none",
  border: "2px solid transparent",
  cursor: "pointer",
  transition: "all 0.3s ease",
  marginTop: "28px",

  "&:hover": {
    backgroundColor: "#000080",
    color: "#ffffff",
    borderColor: "#000080",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 15px rgba(0, 0, 128, 0.3)",
  },

  "&:active": {
    transform: "translateY(0) scale(0.98)",
  },
});

const AccentLine = styled(Box)({
  width: 60,
  height: 4,
  borderRadius: 2,
  background: `linear-gradient(90deg, ${PRIMARY_BLUE}, ${LIGHT_BLUE})`,
  marginBottom: 20,
  backgroundSize: "200% auto",
});

const DecorativeCircle = styled(Box)(({ size, top, left, right, bottom, delay }) => ({
  position: "absolute",
  width: size,
  height: size,
  borderRadius: "50%",
  border: `2px dashed ${BORDER_BLUE}`,
  top,
  left,
  right,
  bottom,
  opacity: 0.5,
  animation: `${rotate} ${25 + (delay || 0)}s linear infinite`,
  pointerEvents: "none",
  zIndex: 1,
}));

const DecorativeBlob = styled(Box)(({ top, left, right, bottom, color }) => ({
  position: "absolute",
  width: 400,
  height: 400,
  borderRadius: "50%",
  background: color,
  filter: "blur(90px)",
  top,
  left,
  right,
  bottom,
  opacity: 0.12,
  pointerEvents: "none",
  zIndex: 0,
}));

// ============================================================
// MAIN COMPONENT
// ============================================================

const AboutComponent = () => {
  return (
    <SectionWrapper>
      {/* Decorative Background Elements */}
      <DecorativeBlob top="-100px" left="-100px" color={PRIMARY_BLUE} />
      <DecorativeBlob bottom="-100px" right="-100px" color={LIGHT_BLUE} />

      {/* Rotating Decorative Circles */}
      <DecorativeCircle size={180} top="5%" right="3%" delay={0} />
      <DecorativeCircle size={120} bottom="10%" left="4%" delay={12} />

      <ContentContainer>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 6, md: 8 },
            alignItems: "center",
          }}
        >
          {/* ============================================
              LEFT: Image + Floating Stats
          ============================================ */}
          <ImageWrapper>
            <Box sx={{ position: "relative" }}>
              <ImageContainer>
                <StyledImage src={aboutImage} alt="About Our Company" />
              </ImageContainer>

              {/* Floating Badge - Top Right */}
              <FloatingCard position="top">
                <FloatingIcon color="blue">
                  <GroupsIcon />
                </FloatingIcon>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: TEXT_MUTED,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      lineHeight: 1.2,
                    }}
                  >
                    Trusted By
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "17px",
                      color: DEEP_BLUE,
                      fontWeight: 800,
                      lineHeight: 1.3,
                    }}
                  >
                    5,000+ Traders
                  </Typography>
                </Box>
              </FloatingCard>

              {/* Floating Badge - Bottom Left */}
              <FloatingCard position="bottom">
                <FloatingIcon color="light">
                  <EmojiEventsIcon />
                </FloatingIcon>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: TEXT_MUTED,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      lineHeight: 1.2,
                    }}
                  >
                    Years Experience
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "17px",
                      color: DEEP_BLUE,
                      fontWeight: 800,
                      lineHeight: 1.3,
                    }}
                  >
                    15+ Combined
                  </Typography>
                </Box>
              </FloatingCard>
            </Box>
          </ImageWrapper>

          {/* ============================================
              RIGHT: Text + CTA
          ============================================ */}
          <TextContent>
            {/* Accent Line */}
            <AccentLine />

            {/* Small Label */}
            <Typography
              sx={{
                color: PRIMARY_BLUE,
                fontWeight: 700,
                fontSize: "13px",
                letterSpacing: "2px",
                textTransform: "uppercase",
                mb: 2,
              }}
            >
              About Our Company
            </Typography>

            {/* Main Heading */}
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: DEEP_BLUE,
                lineHeight: 1.25,
                fontSize: { xs: "1.8rem", sm: "2.1rem", md: "2.5rem" },
                mb: 3,
              }}
            >
              Meet{" "}
              <Box
                component="span"
                sx={{
                  color: PRIMARY_BLUE,
                  position: "relative",
                  display: "inline-block",
                }}
              >
                Our Company
              </Box>
              <br />
              Don't Miss the Opportunity
            </Typography>

            {/* Paragraph */}
            <Typography
              sx={{
                color: TEXT_MUTED,
                lineHeight: 1.8,
                fontSize: "16px",
                mb: 2,
                textAlign: "left",
              }}
            >
              Hey there! We're thrilled you stopped by. Our company is all
              about crafting innovative solutions and delivering exceptional
              results. From strategy to execution, our expert team is
              dedicated to exceeding your expectations.
            </Typography>

            {/* Highlight Points */}
            <Box sx={{ mt: 3, display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip
                icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
                label="Verified Signals"
                size="small"
                sx={{
                  backgroundColor: SOFT_BLUE,
                  color: PRIMARY_BLUE,
                  fontWeight: 600,
                  fontSize: "12px",
                  border: `1px solid ${BORDER_BLUE}`,
                  "& .MuiChip-icon": { color: PRIMARY_BLUE },
                }}
              />
              <Chip
                icon={<EmojiEventsIcon sx={{ fontSize: 16 }} />}
                label="Expert Mentorship"
                size="small"
                sx={{
                  backgroundColor: SOFT_BLUE,
                  color: PRIMARY_BLUE,
                  fontWeight: 600,
                  fontSize: "12px",
                  border: `1px solid ${BORDER_BLUE}`,
                  "& .MuiChip-icon": { color: PRIMARY_BLUE },
                }}
              />
              <Chip
                icon={<GroupsIcon sx={{ fontSize: 16 }} />}
                label="Global Community"
                size="small"
                sx={{
                  backgroundColor: SOFT_BLUE,
                  color: PRIMARY_BLUE,
                  fontWeight: 600,
                  fontSize: "12px",
                  border: `1px solid ${BORDER_BLUE}`,
                  "& .MuiChip-icon": { color: PRIMARY_BLUE },
                }}
              />
            </Box>

            {/* CTA Button */}
            <CTAButton href="/about">
              Explore More
            </CTAButton>
          </TextContent>
        </Box>
      </ContentContainer>
    </SectionWrapper>
  );
};

export default AboutComponent;